/**
 * ============================================================================
 * USER STATS COMPONENT
 * ============================================================================
 * User statistics for profile Stats tab – dashboard-wt theme
 */

import React, { useEffect } from 'react';
import { useUser } from '../../hooks/useUser';
import { useI18n } from '../../providers';
import Spinner from '../common/Spinner';

/**
 * User Stats Component
 * @param {Object} props
 * @param {string} props.userId - User ID (optional, defaults to current user)
 */
const UserStats = ({ userId = null }) => {
  const { loadStats, stats, loading, error } = useUser(userId);
  const { formatCurrency, language } = useI18n();

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading && !stats) {
    return (
      <div className="py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !stats) {
    return (
      <p className="dashboard-wt-card-desc py-6 text-center">
        {language === 'fr' ? 'Impossible de charger les statistiques.' : 'Unable to load statistics.'}
      </p>
    );
  }

  if (!stats) return null;

  const totalTransactions = stats.totalTransactions ?? 0;
  const totalAmount = stats.totalAmount ?? (stats.totalSpent ?? 0) + (stats.totalEarned ?? 0);
  const currency = stats.currency || 'XAF';
  const averageRating = Number(stats.averageRating ?? stats.ratingAverage ?? 0);
  const successfulTransactions = stats.successfulTransactions ?? stats.completedTransactions ?? 0;

  const statCards = [
    {
      label: language === 'fr' ? 'Transactions totales' : 'Total Transactions',
      value: totalTransactions,
      icon: '📊',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      label: language === 'fr' ? 'Montant total' : 'Total Amount',
      value: formatCurrency(totalAmount, currency),
      icon: '💰',
      color: 'from-green-500 to-emerald-500',
    },
    {
      label: language === 'fr' ? 'Note moyenne' : 'Average Rating',
      value: averageRating.toFixed(1),
      icon: '⭐',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      label: language === 'fr' ? 'Transactions réussies' : 'Successful Transactions',
      value: successfulTransactions,
      icon: '✅',
      color: 'from-green-500 to-teal-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat, index) => (
        <div
          key={index}
          className={`dashboard-wt-card p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)] animate-fadeInUp`}
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-2xl shadow-lg flex-shrink-0`}>
              {stat.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#a3a3a3] mb-1">{stat.label}</p>
              <p className="text-xl font-bold text-[#fafafa]">{stat.value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserStats;
